import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosAuth from "../utils/axiosAuth";

// Async thunks
export const fetchBranches = createAsyncThunk(
  "branches/fetchBranches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get("/api/branches/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBranchById = createAsyncThunk(
  "branches/fetchBranchById",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get(`/api/branches/${branchId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBranch = createAsyncThunk(
  "branches/updateBranch",
  async ({ branchId, branchData }, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.put(
        `/api/branches/${branchId}/`,
        branchData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSalesAreas = createAsyncThunk(
  "branches/fetchSalesAreas",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get("/api/sales-areas/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createBranch = createAsyncThunk(
  "branches/createBranch",
  async (branchData, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.post("/api/branches/", branchData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignBranchSupervisors = createAsyncThunk(
  "branches/assignBranchSupervisors",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.post(
        "/api/branches/assign-supervisors/",
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEmployees = createAsyncThunk(
  "branches/fetchEmployees",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get("/api/employees/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPosConfigs = createAsyncThunk(
  "branches/fetchPosConfigs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get("/api/pos-configs");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchResBranches = createAsyncThunk(
  "branches/fetchResBranches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosAuth.get("/api/res-branches");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  branches: [],
  currentBranch: null,
  salesAreas: [],
  employees: [],
  posConfigs: [], // Added for POS config data
  resBranches: [], // Added for Res Branch data
  loading: false,
  error: null,
  updateLoading: false,
  updateError: null,
  createLoading: false,
  createError: null,
};

const branchesSlice = createSlice({
  name: "branches",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
      state.createError = null;
    },
    clearCurrentBranch: (state) => {
      state.currentBranch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch branches
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
        state.error = null;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch branch by ID
      .addCase(fetchBranchById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranchById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBranch = action.payload;
        state.error = null;
      })
      .addCase(fetchBranchById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update branch
      .addCase(updateBranch.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.currentBranch = action.payload;
        // Update the branch in the branches list
        const index = state.branches.findIndex(
          (branch) => branch.id === action.payload.id
        );
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        state.updateError = null;
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Fetch sales areas
      .addCase(fetchSalesAreas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesAreas.fulfilled, (state, action) => {
        state.loading = false;
        state.salesAreas = action.payload;
        state.error = null;
      })
      .addCase(fetchSalesAreas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create branch
      .addCase(createBranch.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.createLoading = false;
        state.branches.push(action.payload);
        state.createError = null;
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })
      // Fetch employees
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload;
        state.error = null;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch POS configs
      .addCase(fetchPosConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPosConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.posConfigs = action.payload;
        state.error = null;
      })
      .addCase(fetchPosConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Res Branches
      .addCase(fetchResBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.resBranches = action.payload;
        state.error = null;
      })
      .addCase(fetchResBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentBranch } = branchesSlice.actions;
export default branchesSlice.reducer;
